import * as React from 'react';
import * as webHelpers from '../../../helpers/WebHelpers';
import * as emailHelpers from '../../../helpers/EmailHelper';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
//@ts-ignore
import {Button, Flex, Text, Heading, ButtonGroup, Grid, GridItem, Link, Box, AlertIcon, Alert, } from '@chakra-ui/react';
import { GetLongDateFormat, GetShortDateFormat, Moment } from '../../../helpers/DateTimeHelpers';
import CustomerDetails, { ICustomerDetails } from '../../components/CustomerDetails';
import { PageContainer, PortalPageType } from '../../components/container';
import { useAuth } from '../../../context/AuthContext.component';
import { IVerbalQuote } from '../../../interfaces/verbalquotes/IVerbalQuote';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';

interface IRequestBody {
    date: string,
    days_ahead: number,
    post_code: string,
    discipline_id: string,
}

interface IQuoteAppointmentBookingProps {
    discipline_id: string,
    post_code: string,
    backCB: Function,
    submitCB: Function,
}

export interface IAppointmentSlot {
    date: string,
    slots: IAppointment[],
}

export interface IAppointment {
    start: string,
    end: string,
}

export default function QuoteBookingPage() {
    const auth = useAuth();

    const [stage, setStage] = React.useState(0); // 0 = initial quote, 1 = info form, 2 = payment, -1 = job already booked
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const { quoteId } = useParams();
    const sendDataToGTM = useGTMDispatch();
    
    const [quote, setQuote] = React.useState<IVerbalQuote | null>(null);

    const [showMoreDays, setShowMoreDays] = React.useState(false);
    const [availableAppointments, setAvailableAppointments] = React.useState<IAppointmentSlot[]>([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Contact details needs its own data structure to reflect the address captured in the quote customer form.
    // this billing info will be passed to the square form once that is implemented fully.
    const [customerDetails, setCustomerDetails] = React.useState<ICustomerDetails>();

    const [selectedApp, setSelectedApp] = React.useState<number>(-1);
    const [selectedSlot, setSelectedSlot] = React.useState<number>(-1);
    const [selectedAppointment, setSelectedAppointment] = React.useState<IAppointment>();

    const [showErrors, setShowErrors] = React.useState<boolean>(false);
    const [customerErrors, setCustomerErrors] = React.useState<string[]>([]);
    const [validationErrors, setValidationErrors] = React.useState<string[]>([]);

    const getQuote = async () => {
        return await webHelpers.getAsync<IVerbalQuote>(
            '/api/sales/quotes/verbal' + (quoteId == null ? "" : "?quote=" + quoteId),
        );
    }

    const getAppointmentSlots = async () => {
        if (quote != null && quote.address.post_code && quote.original_quote.main.lines[0].product != null) {
            let payload = {
                date: Moment().format(),
                days_ahead: 3,
                post_code: quote.address.post_code,
                discipline_id: quote.original_quote.main.lines[0].product.discipline_id,
            }
    
            return await webHelpers.postAsync<IAppointmentSlot[], IRequestBody>(
                '/api/sales/appointment/slot', 
                payload
            );
        }
    }

    const handleBookAppointment = () => {
        setIsSaving(true);
        if (validate()) {
            console.log("No Errors");
            setShowErrors(false);
            bookAppointment();
        } else {
            console.log("Errors found");
            setShowErrors(true);
        }
        setIsSaving(false);
    }

    const bookAppointment = async () => {
        let payload = {
            ... customerDetails,
            appointment: selectedAppointment
        }

        let response = await webHelpers.postAsync<string, any>(
            "/api/sales/quotes/book",
            payload
        );

        if (response != null) {
            let postCode = quote?.address.post_code
            
            if (postCode != undefined) {
                auth.signInCustomer(response, postCode).then(result => {
                    navigate("/customer/overview/" + response);
                });
                sendDataToGTM({event: "quote_booked", jobNumber: response});
            }
        } else {
            setValidationErrors(["Error booking appointment. Please contact customer support."])
            setShowErrors(true);
        }
    }


    const refresh = () => {
        getQuote().then(quote => {
            if (quote != null) {
                console.log(quote);
                setQuote(quote);
                setCustomerDetails({ title: '',
                                    business_name: '',
                                    first_name: '',
                                    last_name: '',
                                    mobile: '',
                                    landline: '',
                                    email: '',
                                    address: {
                                        post_code: quote.address.post_code ? quote.address.post_code : '',
                                        line1: '',
                                        line2: '',
                                        line3: '',
                                        line4: ''
                                    }});
            }
        });
    };

    React.useEffect(() => {
        getAppointmentSlots().then(slots => {
            if (slots != null) {
                setAvailableAppointments(slots);

                setLoading(false);
            }
        });
    }, [ quote ]);

    // React.useEffect(() => {
    //     if (quoteId != undefined) {
    //         refresh();
    //     }
    // }, [ quoteId ]);

    React.useEffect(() => {
        if (quote == null && !loading) {
            setLoading(true);


            if (quoteId != null) {
                console.log(quoteId);
                refresh();
            }
            else {
                navigate("/quote/signout");
            }
        }
    }, []);

    const handleAppSlotClicked = (appIndex: number, slotIndex: number, slot: IAppointment) => {
        setSelectedApp(appIndex);
        setSelectedSlot(slotIndex);
        setSelectedAppointment(slot);
    }

    const updateCustomerDetails = (details: ICustomerDetails, errors: string[]) => {
        setCustomerDetails(details);
        setCustomerErrors(errors);
    }

    const validate = () => {
        let output = true;
        setValidationErrors([]);

        if (customerDetails == undefined) {
            setValidationErrors(["Oops - customer details not found"]);
            output = false;
        }

        if (selectedAppointment == undefined) {
            setValidationErrors(["You must select an appointment slot"]);
            output = false;
        }

        if (customerErrors.length > 0) {
            setValidationErrors(prev => ([...prev, ...customerErrors]));
            output = false;
        }

        return output;
    }

    return (
        <PageContainer pageTitle={'Booking'} type={PortalPageType.Quote}>
            <Flex direction="column" w='full'>
                {availableAppointments.length <= 0 ?
                    <Flex direction="column" mt={8}>
                        <Flex direction="column">
                            <Text fontSize={22} fontWeight='semibold' color='var(--Able-blue)' pl={2}>Appointments</Text>

                            <Flex w='full' justifyContent="center" alignItems="center">
                                <Text>Checking Appointment Slots...</Text>
                            </Flex>
                        </Flex>
                    </Flex>
                :
                    <Flex direction="column" mt={8}>
                        <Flex direction="column">
                            <Text fontSize={22} fontWeight='semibold' color='var(--Able-blue)' pl={2}>Appointments</Text>

                            <Text mt={2} pl={2}>Here are our available slots, for a wider range of appointments or in the event of an emergency please call us on 0330 178 8157</Text>

                            <Flex borderBottom="2px" borderColor='gray.200' mt={4} />

                            
                            <Grid pt={6} gap={2} pl={2} pr={2} templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }}>
                                {availableAppointments.map((app, appIndex) => 
                                    {
                                        if (showMoreDays == false && appIndex > 0) {
                                            return <></>;
                                        }

                                        return (
                                            <GridItem key={appIndex} p={4} border="solid 1px lightgray" borderRadius="md" gridAutoColumns="auto">
                                                <Text>{GetLongDateFormat(app.date)}</Text>

                                                <ButtonGroup size='sm' mt={2} isAttached variant='outline' w='auto'>
                                                    {app.slots.map((slot, slotIndex) => 
                                                        <Button
                                                            key={slotIndex}
                                                            p={4}
                                                            border="solid 1px var(--primary) !important"
                                                            borderRadius="md"
                                                            backgroundColor={
                                                                selectedApp != -1 && selectedSlot != -1 &&
                                                                selectedApp == appIndex && selectedSlot == slotIndex ? 
                                                                "var(--primary)" : "white"
                                                            }
                                                            color={
                                                                selectedApp != -1 && selectedSlot != -1 &&
                                                                selectedApp == appIndex && selectedSlot == slotIndex ? 
                                                                "white" : "var(--primary)"
                                                            }
                                                            _hover={{
                                                                backgroundColor: "var(--primary)",
                                                                color: "var(--primary-foreground)"
                                                            }}
                                                            _active={{
                                                                backgroundColor: "var(--primary)",
                                                                color: "var(--primary-foreground)"
                                                            }}
                                                            onClick={() => handleAppSlotClicked(appIndex, slotIndex, slot)}>
                                                                
                                                                {GetShortDateFormat(slot.start)} - {GetShortDateFormat(slot.end)}
                                                        </Button>
                                                    )}
                                                </ButtonGroup>
                                            </GridItem>  
                                        );
                                    }  
                                )}
                            </Grid>

                            {showMoreDays == false && 
                                <Flex w={{ base: "100%", lg: "50%" }} justifyContent="end">
                                    <Link color="var(--primary)" onClick={() => setShowMoreDays(true)}>Show More Appointments</Link>
                                </Flex>
                            }
                        </Flex>
                    </Flex>
                }

                <Flex direction="column" w='full' mt={8}>
                    <Text fontSize={22} fontWeight='semibold' textColor='var(--Able-blue)' pl={2}>Customer Details</Text>

                    <Flex borderBottom="2px" borderColor='gray.200' mt={4} />

                    {quote && <CustomerDetails isReadOnly={false} updateCustomerDetails={updateCustomerDetails} details={customerDetails} customerType={quote.customer_type}/>}
                </Flex>

                {showErrors &&
                    <Flex w= 'full' direction="column" mt={4}>
                        {validationErrors.slice(0,2).map((error, index) => 
                            <Alert status='error' mb={2}>
                                <AlertIcon/>
                                {error}
                            </Alert>
                        )}
                    </Flex>
                }

                <Flex w='full' justifyContent="flex-end" mt={4}>
                    <Button className='md:ml-16' colorScheme='blue' onClick={handleBookAppointment} isDisabled={isSaving}>
                        Book Appointment
                    </Button>
                </Flex>
            </Flex>
        </PageContainer>
    );
}